import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  formatCalendarDate,
  parseCalendarDate,
  tenantTodayDate,
  toCalendarDateString,
} from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /**
   * Lands on the trigger button, so a `<label htmlFor>` beside the picker
   * actually names the control. Without it the label is decoration: a screen
   * reader reaches an unnamed button, and `getByLabelText` cannot find it
   * either — which is how this gap was found.
   */
  id?: string;
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Parse a date value for DISPLAY and for positioning the calendar grid.
 *
 * Returns null when there is nothing usable. It used to return `new Date()` —
 * today — for both an absent value and an unparseable one, which is the
 * absent-renders-as-a-plausible-wrong-value rule: a malformed stored date
 * showed the user today's date in the field, and today's month in the grid, with
 * nothing to indicate the stored value had not been read. Callers now decide:
 * the display renders empty, and the grid falls back to today explicitly,
 * because a calendar has to open on some month.
 */
function safeParseDate(v: any): Date | null {
  if (!v) return null;
  // Convert to string if it's a Date object or something else
  const str = typeof v === 'string' ? v : String(v);
  // A stored calendar day, in either spelling the API sends: ISO
  // ('2026-04-12T00:00:00.000Z') or Postgres ('2026-04-12 00:00:00+00'). This
  // used to cut the day out with split('T'), which leaves the Postgres spelling
  // whole. That string then fell through to `new Date(str)` below, a UTC
  // instant, and every picker showed the day before in Alberta (fe#814).
  const day = parseCalendarDate(str);
  if (day) return day;
  // Fallback: native parsing, for a value that is not a stored day at all.
  const dt = new Date(str);
  return isNaN(dt.getTime()) ? null : dt;
}

/** Returns '' for an absent OR unreadable value — never today. See safeParseDate. */
function formatDisplay(dateStr: string): string {
  if (!dateStr) return '';
  const dt = safeParseDate(dateStr);
  // safeParseDate hands back a locally-constructed calendar day, so it goes
  // back out through the calendar-day formatter: a day has no zone to apply.
  return dt ? formatCalendarDate(toCalendarDateString(dt)) ?? '' : '';
}

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

type PickerView = 'days' | 'months' | 'years';

export default function DatePicker({ value, onChange, placeholder = 'Select date', className, id }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [pickerView, setPickerView] = useState<PickerView>('days');
  const ref = useRef<HTMLDivElement>(null);

  // "Today" is the TENANT's today (companies.time_zone), never the device's: a
  // dispatcher in Edmonton picking a Chicago crew's date, or a laptop left on
  // UTC after 18:00 Mountain, must see the tenant's day marked. Read through the
  // hook here rather than a prop, so every caller keeps its props unchanged.
  const timeZone = useTenantTimeZone();
  const tenantToday = tenantTodayDate(timeZone);
  // Where the grid opens when there is no readable value. The grid has to open
  // on SOME month; with the zone unknown (no provider — only outside the app's
  // zone gate) it opens on the device's month, which positions the grid and
  // claims nothing: no cell is marked today without a zone.
  const gridAnchor = () => tenantToday ?? new Date();

  // Calendar view state — the month of the current value, or today when there is
  // no readable value. Falling back to today is correct HERE and only here: the
  // grid has to open on some month. It is not correct for the text the user
  // reads, which is why formatDisplay renders empty instead.
  const init = safeParseDate(value) ?? gridAnchor();
  const [viewYear, setViewYear] = useState(init.getFullYear());
  const [viewMonth, setViewMonth] = useState(init.getMonth());
  const [yearRangeStart, setYearRangeStart] = useState(Math.floor(init.getFullYear() / 12) * 12);

  // Sync calendar view when value changes externally
  useEffect(() => {
    const d = safeParseDate(value) ?? tenantTodayDate(timeZone) ?? new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setYearRangeStart(Math.floor(d.getFullYear() / 12) * 12);
  }, [value, timeZone]);

  // Safety net: if viewYear or viewMonth are somehow NaN, reset to today
  const safeYear = isNaN(viewYear) ? gridAnchor().getFullYear() : viewYear;
  const safeMonth = isNaN(viewMonth) ? gridAnchor().getMonth() : viewMonth;

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setPickerView('days');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const prevMonth = () => {
    if (safeMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (safeMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const selectDay = (year: number, month: number, day: number) => {
    onChange(`${year}-${pad(month + 1)}-${pad(day)}`);
    setOpen(false);
    setPickerView('days');
  };

  const selectMonth = (month: number) => {
    setViewMonth(month);
    setPickerView('days');
  };

  const selectYear = (year: number) => {
    setViewYear(year);
    setYearRangeStart(Math.floor(year / 12) * 12);
    setPickerView('months');
  };

  // Build calendar grid
  const totalDays = daysInMonth(safeYear, safeMonth);
  const firstDow = new Date(safeYear, safeMonth, 1).getDay(); // 0=Sun
  const prevMonthDays = daysInMonth(safeYear, safeMonth === 0 ? 11 : safeMonth - 1);

  const cells: { day: number; month: number; year: number; current: boolean }[] = [];

  // Leading days from previous month
  const prevY = safeMonth === 0 ? safeYear - 1 : safeYear;
  const prevM = safeMonth === 0 ? 11 : safeMonth - 1;
  for (let i = firstDow - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, month: prevM, year: prevY, current: false });
  }

  // Current month days
  for (let d = 1; d <= totalDays; d++) {
    cells.push({ day: d, month: safeMonth, year: safeYear, current: true });
  }

  // Trailing days from next month
  const nextY = safeMonth === 11 ? safeYear + 1 : safeYear;
  const nextM = safeMonth === 11 ? 0 : safeMonth + 1;
  const remaining = 42 - cells.length; // 6 rows
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, month: nextM, year: nextY, current: false });
  }

  // Null when the zone is unknown: no cell and no year is then marked as
  // current, rather than the device's day standing in for the tenant's.
  const todayStr = tenantToday ? toCalendarDateString(tenantToday) : null;
  const currentYear = tenantToday ? tenantToday.getFullYear() : null;

  // The selected cell, as 'YYYY-MM-DD'. It comes from the same parse as the
  // trigger's text, so the day the field shows is the day the calendar marks. It
  // used its own split('T') copy, and a Postgres-style value never matched any
  // cell, so nothing was marked (fe#814).
  const selectedDate = safeParseDate(value);
  const normalizedValue = selectedDate ? toCalendarDateString(selectedDate) : '';

  // Year grid: 12 years at a time
  const safeYearRangeStart = isNaN(yearRangeStart) ? Math.floor(gridAnchor().getFullYear() / 12) * 12 : yearRangeStart;
  const years: number[] = [];
  for (let y = safeYearRangeStart; y < safeYearRangeStart + 12; y++) {
    years.push(y);
  }

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        id={id}
        type="button"
        onClick={() => { setOpen(o => !o); setPickerView('days'); }}
        className={className || 'w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none flex items-center justify-between gap-2 text-left'}
      >
        <span className={value ? 'text-white' : 'text-fieldiq-muted'}>
          {/* An unreadable stored value falls through to the placeholder rather
              than to today's date. formatDisplay returns '' for both absent and
              unparseable, so one `||` covers the two cases the old code
              conflated — it rendered today for both. */}
          {formatDisplay(value) || placeholder}
        </span>
        <Calendar size={14} className="text-fieldiq-muted flex-shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-72 bg-fieldiq-card border border-fieldiq-border rounded-xl shadow-2xl overflow-hidden animate-fade-in">

          {/* ─── Year Picker ─── */}
          {pickerView === 'years' && (
            <>
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-fieldiq-border">
                <button type="button" onClick={() => setYearRangeStart(y => y - 12)} className="p-1 rounded hover:bg-fieldiq-dark text-fieldiq-text hover:text-white transition">
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm font-semibold text-white">
                  {safeYearRangeStart} – {safeYearRangeStart + 11}
                </span>
                <button type="button" onClick={() => setYearRangeStart(y => y + 12)} className="p-1 rounded hover:bg-fieldiq-dark text-fieldiq-text hover:text-white transition">
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 p-3">
                {years.map(y => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => selectYear(y)}
                    className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                      y === safeYear
                        ? 'bg-fieldiq-accent text-fieldiq-dark font-bold'
                        : y === currentYear
                          ? 'text-fieldiq-accent hover:bg-fieldiq-border'
                          : 'text-white hover:bg-fieldiq-border'
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ─── Month Picker ─── */}
          {pickerView === 'months' && (
            <>
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-fieldiq-border">
                <button type="button" onClick={() => { setViewYear(y => y - 1); }} className="p-1 rounded hover:bg-fieldiq-dark text-fieldiq-text hover:text-white transition">
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setPickerView('years')}
                  className="text-sm font-semibold text-white hover:text-fieldiq-accent transition-colors"
                >
                  {safeYear}
                </button>
                <button type="button" onClick={() => { setViewYear(y => y + 1); }} className="p-1 rounded hover:bg-fieldiq-dark text-fieldiq-text hover:text-white transition">
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 p-3">
                {MONTHS_SHORT.map((m, i) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => selectMonth(i)}
                    className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                      i === safeMonth && safeYear === currentYear
                        ? 'bg-fieldiq-accent text-fieldiq-dark font-bold'
                        : i === safeMonth
                          ? 'bg-fieldiq-accent text-fieldiq-dark font-bold'
                          : 'text-white hover:bg-fieldiq-border'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ─── Day Picker ─── */}
          {pickerView === 'days' && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-fieldiq-border">
                <button type="button" onClick={prevMonth} className="p-1 rounded hover:bg-fieldiq-dark text-fieldiq-text hover:text-white transition">
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setPickerView('months')}
                  className="text-sm font-semibold text-white hover:text-fieldiq-accent transition-colors"
                >
                  {MONTHS[safeMonth]} {safeYear}
                </button>
                <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-fieldiq-dark text-fieldiq-text hover:text-white transition">
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Weekday headers */}
              <div className="grid grid-cols-7 px-2 pt-2">
                {DAYS.map(d => (
                  <div key={d} className="text-center text-[10px] font-medium text-fieldiq-muted py-1">{d}</div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7 px-2 pb-2">
                {cells.map((cell, i) => {
                  const cellStr = `${cell.year}-${pad(cell.month + 1)}-${pad(cell.day)}`;
                  const isSelected = cellStr === normalizedValue;
                  const isToday = todayStr !== null && cellStr === todayStr;

                  return (
                    <button
                      key={i}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => selectDay(cell.year, cell.month, cell.day)}
                      className={`
                        w-full aspect-square flex items-center justify-center text-xs rounded-lg transition-colors relative
                        ${isSelected
                          ? 'bg-fieldiq-accent text-fieldiq-dark font-bold'
                          : cell.current
                            ? 'text-white hover:bg-fieldiq-border'
                            : 'text-fieldiq-muted/40 hover:bg-fieldiq-border/50'
                        }
                      `}
                    >
                      {cell.day}
                      {isToday && !isSelected && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-fieldiq-accent" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
